<template lang="pug">
.vprTaskPlayerComponent
  vpr-player-background( :subject="subject" )
  .content(v-if="task")
    .buttonWrapper.prev(v-show="isOrientationLandscape")
      ui-button.button(
        v-if="task.prevTask"
        size="xl"
        is-responsive
        color="blue-outline"
        variant="3d"
        @mouseover="preloadState(task.prevTask)"
        @click="$router.push({name: 'VprTask', params: {taskId: task.prevTask.id}})"
      )
        ui-svg-icon.icon(
          name="arrow-button"
          :size="26"
        )
    .playerWrapper(ref="iframeWrapperRef")
      iframe.iframe(
        :src="iframeUrl"
        loading="lazy"
        ref="iframeRef"
        :style="iframeStyle"
        v-show="isOrientationLandscape"
      )
      .orientation(v-show="!isOrientationLandscape")
        .icon
          ui-svg-icon( name="phone-rotate" )
        .text Поверните ваше устройство для&nbsp;комфортного отображения заданий
        ui-button.close(
          variant="3d"
          @click="goToVprControlworks"
        )
          ui-svg-icon.icon(
            name="close"
            :size="16"
          )
    .buttonWrapper.next(v-show="isOrientationLandscape")
      ui-button.button(
        v-if="task.nextTask"
        size="xl"
        is-responsive
        color="blue-outline"
        variant="3d"
        @mouseover="preloadState(task.nextTask)"
        @click="$router.push({ name: 'VprTask', params: { taskId: task.nextTask.id } })"
      )
        ui-svg-icon.icon(
          name="arrow-button"
          :size="26"
        )
</template>

<script>
import { computed, inject, onUnmounted, ref, watch } from 'vue'
import clamp from '@/_core/helpers/clamp'
import VprPlayerBackground from '../VprPlayerBackground'
import PlayerModalClose from '@/components/Player/PlayerModalClose/PlayerModalClose'
import PlayerTaskController from '@/controllers/ChildVpr/PlayerTaskController'
import router from '@/router'
import debounce from '@/_core/helpers/debounce.js'
import screenOrientation from 'screen-orientation-2'

export default {
  components: {
    VprPlayerBackground,
    PlayerModalClose
  },
  props: {
    taskId: {
      type: Number,
      required: true
    }
  },
  setup (props) {
    const store = inject('store')

    const controller = inject('controller')

    const isLoading = controller.isTaskLoading

    const iframeRef = ref(null)
    const iframeWrapperRef = ref(null)

    // Iframe scaling.
    const IFRAME_WIDTH = 900
    const IFRAME_HEIGHT = 800

    const calculateScaleModifier = () => {
      if (!iframeRef.value || !iframeWrapperRef.value) return false

      const wrapperWidth = iframeWrapperRef.value.offsetWidth
      const wrapperHeight = iframeWrapperRef.value.offsetHeight

      const scaleX = wrapperWidth / IFRAME_WIDTH
      const scaleY = wrapperHeight / IFRAME_HEIGHT

      const scale = clamp(Math.min(scaleX, scaleY), 0, 1)

      cssScale.value = scale
    }

    const cssScale = ref(1)

    const iframeStyle = computed(() => {
      return {
        transform: `scale(${cssScale.value})`,
        width: `calc(100% / ${cssScale.value})`,
        height: `calc(100% / ${cssScale.value})`
      }
    })

    window.addEventListener('resize', calculateScaleModifier)

    watch(iframeRef, calculateScaleModifier)

    const { task, initIframeListener, destroyIframeListener, $on, $off } = PlayerTaskController(
      controller,
      computed(() => props.taskId)
    )

    const iframeUrl = ref(null)
    const initIframeUrl = () => {
      iframeUrl.value = `${task.value?.iframeUrl}${task.value?.isDone ? '?auto_load=false' : ''}`
    }
    watch(
      () => props.taskId,
      () => {
        initIframeUrl()
      }
    )

    watch(
      isLoading,
      isLoading => {
        if (!isLoading) {
          // initialize iframe
          initIframeUrl()
          setTimeout(() => {
            initIframeListener(iframeRef.value)
          }, 0)
        } else {
          // destroy iframe
          setTimeout(() => {
            destroyIframeListener()
          }, 0)
        }
      },
      { immediate: true }
    )

    const showCloseModal = () => {
      store.modalQueue.push({
        key: 'vprclose',
        component: PlayerModalClose,
        params: {
          isClosable: false
        },
        props: {
          title: 'Вы уверены, что хотите выйти?'
        },
        on: {
          submit: goToVprControlworks
        }
      })
    }

    const goToVprControlworks = () => router.push({ name: 'VprControlworks' })

    $on('iframe-close-click', showCloseModal)

    // screen orientation
    const orientation = ref(screenOrientation())
    const isOrientationLandscape = computed(() => orientation.value.direction === 'landscape')
    screenOrientation.addEventOnOrientationChange(newOrientation => {
      orientation.value = newOrientation
    })

    onUnmounted(() => {
      destroyIframeListener()
      $off('iframe-close-click', showCloseModal)
      window.removeEventListener('resize', calculateScaleModifier)
      screenOrientation.removeEventOnOrientationChange()
    })

    return {
      task,
      iframeRef,
      iframeWrapperRef,
      iframeUrl,
      iframeStyle,
      subject: computed(() => controller.childVpr.value.userItem.vpr.subject),
      isLoading,
      preloadState: debounce(controller.preloadTaskStateRequest, 200),
      isOrientationLandscape,
      goToVprControlworks
    }
  }
}
</script>

<style lang="sass" scoped>
.vprTaskPlayerComponent
  position: fixed
  width: 100%
  height: 100%
  top: 0
  right: 0
  left: 0
  bottom: 0
  background-color: $cBackground
  z-index: $zIndexPlayerIframe
  display: flex

  .content
    display: flex
    flex-wrap: wrap
    align-items: center
    justify-content: center
    @include gap(5*$u)
    margin: auto
    @media (max-width: $MWidth)
      margin: 0
      align-items: initial
      @include gap(0)

    > .playerWrapper
      flex: 1
      width: min(1100px, 100vw - 2*(#{14*$u} + #{5*$u}) )
      height: min(700px, 100vh)
      border-radius: $brXXL
      background-color: $cWhite
      padding: 5*$u
      position: relative
      @media (max-width: $MWidth)
        width: 100vw
        height: min(700px, 100% - #{17*$u})
        flex: initial
        order: 1
        border-radius: 0
        padding: 0

      > .iframe
        width: 900px
        height: 800px
        display: block
        transform-origin: top left

      > .loader
        position: absolute
        top: 0
        right: 0
        bottom: 0
        left: 0

      > .orientation
        position: relative
        z-index: 1
        display: flex
        flex-direction: column
        align-items: center
        justify-content: center
        height: 100%
        padding: 5vw
        background: $cWhite
        @media (max-width: $MWidth)
          height: 100vh
        > .icon
          margin-bottom: 5*$u
          width: 80px
          height: 73px
          color: $cBlue
        > .text
          text-align: center
          color: $cBlue
          @include font('t18-regular')
          max-width: 95*$u
          margin: 0 auto
          @media (max-width: $padWidth)
            @include font('m-t18-regular')
        > .close
          position: absolute
          top: 3*$u
          right: 3*$u
          border-radius: 3*$u
          flex: 0 0 10*$u
          width: 10*$u
          height: 10*$u

    > .buttonWrapper
      flex: 0 0 13*$u
      width: 13*$u
      align-self: flex-end

    > .prev
      @media (max-width: $MWidth)
        order: 2
        margin: 0 auto 2*$u 2*$u
    > .next
      @media (max-width: $MWidth)
        order: 3
        margin: 0 2*$u 2*$u auto
      &:deep(.icon)
        transform-origin: center
        transform: rotate(180deg)
</style>
